/**
 * Snapshots API — contract #19/#20 (docs/05-dev-spec.md §4, task T.C4b).
 *
 *   #19 POST /api/snapshots/upload → `{ data: SnapshotDto }` (ADMIN, multipart .7z/.xlsx)
 *   #20 GET  /api/snapshots        → `{ data: { snapshots: [SnapshotDto], sourcesHealth } }` (ADMIN)
 *
 * CONTRACT-FIRST: T.B4 (the api module) does not exist yet — this is the shape
 * the И5 admin page is built against, mirrored 1:1 from the generated
 * `packages/shared/src/openapi/api-types.ts` (`SnapshotDto`, `SnapshotListDto`,
 * `SourcesHealthDto`, `DuplicateSnapshotError`) and wired to the routes the
 * contract freezes. When T.B4 lands, only the e2e stubs are dropped; the page
 * and this module are already speaking the real contract.
 *
 * The one field the error envelope adds is `error.existingSnapshotId` — the
 * ratified (additive) carrier of «this file is already ingested as snapshot №N»,
 * which `ApiError` does not preserve. The upload therefore reads the error body
 * itself and throws {@link SnapshotUploadError}, so the page can show the
 * existing snapshot instead of a bare "conflict".
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_BASE_URL, apiRequest, type ApiErrorCode } from './client';
import { parseErrorBody } from './envelope';

export type SnapshotKind = 'EPGU' | 'EVI';
export type SnapshotSource = 'MAILBOX' | 'MANUAL';

/**
 * ADDITIVE v1.8.6 (T.Z40) — the apply-pipeline state of ONE snapshot
 * (openapi `SnapshotApplyState`), projected api-side from the `sync_state` ledger.
 * Every field but `state` is OPTIONAL on the wire: absence is «no such fact», never zero.
 */
export interface SnapshotApplyState {
  state: 'QUEUED' | 'DONE' | 'FAILED';
  /** QUEUED: when the apply was queued. FAILED: when the last attempt failed. */
  lastAttemptAt?: string | null;
  /** DONE only — when the apply committed. */
  finishedAt?: string | null;
  /** FAILED only — drain attempts so far. */
  attempts?: number;
  /** FAILED only — the worker's machine code (`APPLY_FAILED`). */
  errorCode?: string | null;
  /** DONE only — accepted rows the engine applied. */
  appliedRows?: number;
  /** DONE only — applications created / merged / taken through the update path. */
  createdApps?: number;
  mergedApps?: number;
  knownApps?: number;
  /** DONE only — rows that left the ЕВИ queue since the previous ЕВИ snapshot. */
  disappearedRows?: number;
  /** DONE only — funnel transitions / `application_events` the apply wrote. */
  statusChanges?: number;
  events?: number;
}

/** Frozen field set of #19/#20 (openapi `SnapshotDto`; `sha256` stays server-side). */
export interface SnapshotDto {
  id: number;
  kind: SnapshotKind;
  source: SnapshotSource;
  fileName: string;
  /** Parsed from the filename / mail subject (ISO-8601), or null when unparsed. */
  exportDate: string | null;
  /** ISO-8601. */
  receivedAt: string;
  rowsTotal: number;
  rowsAccepted: number;
  /** The quarantine bucket of `packages/ingest` — rows kept as evidence with pguId null. */
  rowsRejected: number;
  ingestError: string | null;
  /**
   * ADDITIVE v1.8.6 (T.Z40) — where the snapshot stands in the apply pipeline.
   * OPTIONAL on the wire (an older api answers without it): absent reads as
   * «no apply state», never as an error.
   */
  apply?: SnapshotApplyState | null;
}

/** `SourcesHealthDto` — the freshest evidence per funnel source. */
export interface SourceSnapshotHealth {
  lastSnapshotAt: string | null;
}

export interface SourcesHealthDto {
  EPGU: SourceSnapshotHealth;
  EVI: SourceSnapshotHealth;
  FTP: { lastSyncAt: string | null };
}

/** The #20 payload — the ledger plus per-source freshness. */
export interface SnapshotListDto {
  snapshots: SnapshotDto[];
  sourcesHealth: SourcesHealthDto;
  /**
   * ADDITIVE v1.8.6 (T.Z40) — the stalled-apply threshold the api runs with
   * (`SNAPSHOT_APPLY_STALE_MS`). Optional on the wire: an older api answers
   * without it and the page falls back to its own documented default.
   */
  applyStaleAfterMs?: number;
}

export type SnapshotListEnvelope = { data: SnapshotListDto };

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export const SNAPSHOTS_PATH = '/snapshots';
export const SNAPSHOTS_UPLOAD_PATH = '/snapshots/upload';
/** ADDITIVE v1.6.1 — the no-file «Обновить данные» (the worker polls the mailbox). */
export const SNAPSHOTS_REFRESH_PATH = '/snapshots/refresh';

/** The ADDITIVE v1.6.1 refresh answer — `requested`, never «done» (the worker polls). */
export interface SnapshotRefreshDto {
  requested: boolean;
  note: string;
}

/**
 * #20 — the RAW answer of `GET /snapshots`. Deliberately un-normalised: the
 * tolerant unwrap ({data: …} vs a bare list, the docs' «[SnapshotDto]» shorthand)
 * is pure logic, and this module value-imports `client.ts` (`import.meta.env`),
 * which the `unit` Playwright project cannot load. It lives in
 * `pages/settings/snapshots-model.ts` as `parseSnapshotList`, where it is unit-tested —
 * the same split that puts `unwrapEnvelope` in the framework-free `envelope.ts`.
 */
export async function fetchSnapshots(signal?: AbortSignal): Promise<unknown> {
  // `apiRequest` (not the local fetch) so a dead session behaves like everywhere
  // else in the app: a 401 is a full-page hop to the OAuth entry point.
  return apiRequest<unknown>('GET', SNAPSHOTS_PATH, { signal });
}

/**
 * A #19/#20 failure that keeps what `ApiError` drops: the HTTP status, the
 * documented code, and — for the 409 — the id of the snapshot that already
 * holds this file's sha256.
 */
export class SnapshotUploadError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly existingSnapshotId?: number,
  ) {
    super(message);
    this.name = 'SnapshotUploadError';
  }

  /** True for the documented code (or its HTTP status, as a fallback). */
  is(code: ApiErrorCode, status?: number): boolean {
    return this.code === code || (status !== undefined && this.status === status);
  }
}

/**
 * #19 — one multipart upload. The transport is a plain `fetch` on purpose: the
 * browser must set the multipart boundary itself (no `Content-Type` header of
 * ours), and the error body has to be read in full to keep
 * `existingSnapshotId`, which the shared `ApiError` normalises away.
 */
export async function uploadSnapshot(file: File, signal?: AbortSignal): Promise<SnapshotDto> {
  const form = new FormData();
  form.set('file', file, file.name);

  const response = await fetch(`${API_BASE_URL}${SNAPSHOTS_UPLOAD_PATH}`, {
    method: 'POST',
    signal,
    body: form,
  });

  if (!response.ok) {
    const { code, message, existingSnapshotId } = await readUploadError(response);
    throw new SnapshotUploadError(response.status, code, message, existingSnapshotId);
  }

  const body = (await response.json()) as Record<string, unknown>;
  const data = body['data'] && typeof body['data'] === 'object' ? body['data'] : body;
  return data as SnapshotDto;
}

/**
 * The error body, read ONCE: the code and message go through the shared
 * normaliser, and `error.existingSnapshotId` (the ratified additive field of the
 * 409) is lifted out before the body is dropped. A non-JSON body (proxy HTML, an
 * empty reply) leaves the status-derived code standing.
 */
async function readUploadError(response: Response): Promise<{
  code: ApiErrorCode;
  message: string;
  existingSnapshotId?: number;
}> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON — `parseErrorBody` falls back to the status-derived code.
  }
  const { code, message } = parseErrorBody(body, response.status);
  const error = isRecord(body) && isRecord(body['error']) ? body['error'] : undefined;
  const existing = error?.['existingSnapshotId'];
  return { code, message, existingSnapshotId: typeof existing === 'number' ? existing : undefined };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const SNAPSHOTS_ROOT = ['snapshots'] as const;

/**
 * #20 — the journal. The cadence is the admin's choice (`snapshots-model.ts`,
 * persisted in `localStorage`): OFF unless they asked for a live view, because a
 * poll nobody asked for is background noise on the api.
 *
 * T.Z40 — `pipelinePoll` adds a LIGHT extra cadence while the payload itself says
 * an apply is still queued: watching a run finish is the one reason to poll with
 * «Автообновление» off. It receives the RAW payload and answers `false` when
 * nothing waits, so the poll arms and disarms itself with the pipeline.
 *
 * The interval is deliberately NOT in the `queryKey`: changing the cadence must
 * re-time the polling, not throw the journal away and re-render the skeleton.
 */
export function useSnapshots(refreshIntervalMs: number = 0, pipelinePoll?: (body: unknown) => number | false) {
  return useQuery({
    queryKey: SNAPSHOTS_ROOT,
    queryFn: ({ signal }) => fetchSnapshots(signal),
    refetchInterval: (query): number | false => {
      // 0 means off: TanStack reads `false` as "never", any positive number as ms.
      if (refreshIntervalMs > 0) return refreshIntervalMs;
      return pipelinePoll ? pipelinePoll(query.state.data) : false;
    },
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}

/**
 * #19 — the upload. A success refetches the journal (the new snapshot is the
 * newest row); a failure is re-thrown as {@link SnapshotUploadError} so the
 * page can render `existingSnapshotId` instead of a generic conflict.
 */
export function useSnapshotUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadSnapshot(file),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOTS_ROOT });
    },
  });
}

/**
 * ADDITIVE v1.6.1 — «Обновить данные». POSTs the no-file refresh and answers
 * 202: the api has only queued a `MAIL_POLL_REQUEST`, so the journal refetch is
 * the button's own optimism, not proof the mailbox has been read. A failure is
 * re-thrown as {@link SnapshotRefreshError} so the page can show the code.
 */
export class SnapshotRefreshError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'SnapshotRefreshError';
  }
}

export async function requestSnapshotRefresh(signal?: AbortSignal): Promise<SnapshotRefreshDto> {
  const response = await fetch(`${API_BASE_URL}${SNAPSHOTS_REFRESH_PATH}`, { method: 'POST', signal });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // Non-JSON — `parseErrorBody` falls back to the status-derived code.
    }
    const { code, message } = parseErrorBody(body, response.status);
    throw new SnapshotRefreshError(response.status, code, message);
  }

  const payload = (await response.json()) as Record<string, unknown>;
  return (payload['data'] && typeof payload['data'] === 'object' ? payload['data'] : payload) as SnapshotRefreshDto;
}

export function useSnapshotRefresh() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => requestSnapshotRefresh(),
    // The new letters are not on the wire yet when the 202 lands, but the ask is:
    // refetch so the journal is at least as fresh as the click that asked for it.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: SNAPSHOTS_ROOT });
    },
  });
}
